import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import QRCode from "qrcode";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { FileText, Copy, Send, Link2, Inbox, Eye, Lock, QrCode, Download } from "lucide-react";
import { toast } from "sonner";

const routeFor = (key) => "/" + key.replace(/_/g, "-");

export default function FormsAdmin() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [detail, setDetail] = useState(null);
  const [qr, setQr] = useState(null);
  const canManage = ["owner", "admin"].includes(user?.role);
  const canView = ["owner", "admin", "practitioner"].includes(user?.role);

  const { data: forms = [] } = useQuery({ queryKey: ["forms"], queryFn: async () => (await api.get("/forms")).data });
  const { data: responses = [] } = useQuery({ queryKey: ["form-responses"], queryFn: async () => (await api.get("/form-responses")).data });

  const toggleMut = useMutation({
    mutationFn: async ({ id, active }) => (await api.put(`/forms/${id}/toggle`, { active })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["forms"] }); toast.success("Form updated"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const sendMut = useMutation({
    mutationFn: async (template_key) => (await api.post("/forms/send", { template_key })).data,
    onSuccess: () => toast.success("Form dispatch queued via n8n (WhatsApp + email)"),
    onError: (e) => toast.error(apiErr(e)),
  });

  const publicLink = (key) => `${window.location.origin}${routeFor(key)}?p=${user?.practice_id}`;
  const copy = (key) => { navigator.clipboard.writeText(publicLink(key)); toast.success("Public link copied"); };

  const openDetail = async (rid) => {
    try {
      const { data } = await api.get(`/form-responses/${rid}`);
      setDetail(data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const showQr = async (f) => {
    const link = publicLink(f.template_key);
    const dataUrl = await QRCode.toDataURL(link, { width: 320, margin: 2 });
    setQr({ dataUrl, link, title: f.title });
  };

  const downloadPdf = async (rid) => {
    try {
      const res = await api.get(`/form-responses/${rid}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight sm:text-3xl">
          <FileText className="h-7 w-7 text-primary" /> Forms
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">Share intake, medical-history and consent forms with patients. Submissions auto-onboard where enabled.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {forms.map((f) => (
          <Card key={f.id} data-testid={`form-${f.template_key}`} className="rounded-2xl border-border/60 p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold">{f.title}</div>
                <div className="text-xs text-muted-foreground">/{f.template_key.replace(/_/g, "-")}{f.auto_onboard ? " · auto-onboard" : ""}</div>
              </div>
              {canManage ? (
                <Switch data-testid={`toggle-${f.template_key}`} checked={f.active} onCheckedChange={(active) => toggleMut.mutate({ id: f.id, active })} />
              ) : (
                <Badge variant={f.active ? "secondary" : "outline"} className="rounded-full">{f.active ? "Active" : "Off"}</Badge>
              )}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" data-testid={`copy-${f.template_key}`} onClick={() => copy(f.template_key)} className="gap-1 rounded-full">
                <Copy className="h-3.5 w-3.5" /> Copy link
              </Button>
              <a href={publicLink(f.template_key)} target="_blank" rel="noreferrer">
                <Button size="sm" variant="ghost" className="gap-1 rounded-full"><Link2 className="h-3.5 w-3.5" /> Preview</Button>
              </a>
              <Button size="sm" variant="outline" data-testid={`qr-${f.template_key}`} onClick={() => showQr(f)} className="gap-1 rounded-full">
                <QrCode className="h-3.5 w-3.5" /> QR
              </Button>
              <Button size="sm" data-testid={`send-${f.template_key}`} onClick={() => sendMut.mutate(f.template_key)} className="gap-1 rounded-full">
                <Send className="h-3.5 w-3.5" /> Send
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <div>
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold"><Inbox className="h-5 w-5 text-primary" /> Recent submissions</h2>
        <Card className="overflow-hidden rounded-2xl border-border/60">
          {responses.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">No submissions yet.</div>
          ) : (
            <div className="divide-y divide-border/60">
              {responses.map((r) => (
                <div key={r.id} data-testid={`response-${r.id}`} className="flex flex-wrap items-center gap-4 px-5 py-3.5">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{r.form_title}</div>
                    <div className="text-xs text-muted-foreground">{r.submitted_by_phone || r.submitted_by_email || "anonymous"} · {new Date(r.submitted_at).toLocaleString("en-ZA")}</div>
                  </div>
                  <Badge variant={r.onboarding_status === "onboarded" ? "secondary" : "outline"} className="rounded-full">{r.onboarding_status}</Badge>
                  {canView ? (
                    <Button size="sm" variant="ghost" data-testid={`view-${r.id}`} onClick={() => openDetail(r.id)} className="gap-1 rounded-full"><Eye className="h-3.5 w-3.5" /> View</Button>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" /> Clinical</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="bg-popover max-h-[80vh] overflow-auto">
          <DialogHeader><DialogTitle>{detail?.form_title}</DialogTitle></DialogHeader>
          {detail && (
            <>
            <div className="mb-2 flex justify-end">
              <Button size="sm" variant="outline" data-testid="response-pdf" onClick={() => downloadPdf(detail.id)} className="gap-1 rounded-full"><Download className="h-3.5 w-3.5" /> Download PDF</Button>
            </div>
            <dl className="space-y-2 text-sm">
              {Object.entries(detail.responses || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-border/40 py-1.5">
                  <dt className="capitalize text-muted-foreground">{k.replace(/_/g, " ")}</dt>
                  <dd className="text-right font-medium">{Array.isArray(v) ? v.join(", ") : String(v)}</dd>
                </div>
              ))}
              <div className="pt-2 text-xs text-muted-foreground">Submitted {new Date(detail.submitted_at).toLocaleString("en-ZA")}</div>
            </dl>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!qr} onOpenChange={(v) => !v && setQr(null)}>
        <DialogContent className="bg-popover">
          <DialogHeader><DialogTitle>{qr?.title} — scan to open</DialogTitle></DialogHeader>
          {qr && (
            <div className="text-center">
              <img src={qr.dataUrl} alt="QR code" width={240} height={240} data-testid="qr-image" className="mx-auto rounded-xl border border-border/60 bg-white p-2" />
              <div className="mt-3 break-all text-xs text-muted-foreground">{qr.link}</div>
              <a href={qr.dataUrl} download={`${qr.title}-qr.png`}>
                <Button variant="outline" className="mt-4 gap-1 rounded-full"><Download className="h-4 w-4" /> Download poster PNG</Button>
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
